import { useState, useEffect, useRef, useCallback } from 'react';
import '../App.css';
import styles from '../assets/styles/relay.module.css';
import { useAuth, db } from '../lib/firebase';
import { doc, setDoc, getDoc, collection, runTransaction } from 'firebase/firestore';
import {
    STREAMS,
    getStreamQueue,
    mergeIntoQueue,
    removeFromQueue,
    clearQueue,
    exportQueue,
    updateRecord,
    getMatchTypeLabel,
    getPositionLabel,
    getFirebaseUploadedIds,
    markFirebaseUploaded,
    pendingForFirebase
} from '../lib/queue';
import { getAcceptedIds, getRejectedIds, piKey } from '../lib/octopus';
import Backarrow from '../components/Backarrow';
import Toast from '../lib/toast';
import { SUBMISSION_ID_KEY } from '../lib/submissionId';
import { checkUploaded, getChecks, saveChecks } from '../lib/firebaseCheck';
import { toast } from 'react-toastify';
import editImg from '../assets/images/edit.png';

/**
 * Where each queue goes in a match document - the same maps Osprey's own uploads write
 * (uploadObject: normal_data, uploadTimekeepData: timekeeper_data), which Neptune reads
 * side by side and joins by match, alliance and position.
 */
const FIRESTORE_FIELD = { scouting: 'normal_data', timekeeper: 'timekeeper_data' };

/**
 * Write one queued record to its slot. An unedited record never overwrites a slot already
 * there (someone may have fixed it in Firebase); an edited one does - an edit is a
 * correction. @returns {Promise<'written'|'exists'>}
 */
async function writeToFirestore(dataObject, eventName, gamemode, stream = 'scouting', { edited = false } = {}) {
    const cleanData = { ...dataObject };
    delete cleanData['version'];
    delete cleanData[SUBMISSION_ID_KEY]; // for the Pi only - Neptune would show it as a column
    delete cleanData._edited_at;

    const year = eventName.substring(0, 4);
    const eventCode = eventName.substring(4);
    const matchKey = `${parseInt(cleanData.match_number) + (gamemode + 1) * 1000}-${cleanData.alliance_color}`;
    const id = `${cleanData.scout_team}-${cleanData.scout_id}-${cleanData.alliance_color}${cleanData.team_position}`;
    const legacyYear = parseInt(year) === 2025 || parseInt(year) === 9999;

    if (stream === 'timekeeper') {
        // Timekeeper only exists in the new layout, as uploadTimekeepData enforces.
        if (legacyYear) throw new Error(`Timekeeper data cannot be uploaded to ${year}`);
        const docRef = doc(db, 'Competitions', 'Years', year, eventCode, 'Final', matchKey);
        return writeSlot(docRef, FIRESTORE_FIELD.timekeeper, id, cleanData, edited);
    }

    if (legacyYear) {
        const unsortedRef = collection(db, 'Competitions', 'Years', year, eventCode, 'Final', matchKey, 'Unsorted');
        return writeSlot(doc(unsortedRef, id), null, id, cleanData, edited);
    }
    const docRef = doc(db, 'Competitions', 'Years', year, eventCode, 'Final', matchKey);
    return writeSlot(docRef, FIRESTORE_FIELD.scouting, id, cleanData, edited);
}

/** field null: the legacy layout, one document per slot. */
async function writeSlot(docRef, field, id, data, overwrite) {
    const value = field ? { [field]: { [id]: data } } : data;
    const options = field ? { merge: true } : undefined;
    if (overwrite) {
        await setDoc(docRef, value, options);
        return 'written';
    }
    return runTransaction(db, async (tx) => {
        const snap = await tx.get(docRef);
        const present = field ? snap.exists() && snap.data()?.[field]?.[id] !== undefined : snap.exists();
        if (present) return 'exists';
        if (options) tx.set(docRef, value, options);
        else tx.set(docRef, value);
        return 'written';
    });
}

function parseQueueFile(text) {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return [parsed];
    return [];
}

const RELAY_TOAST_ID = 'relay-upload-toast';

// The Pi's accepted list changes in the background (the status chip sends every 10 s),
// so the Sent column re-reads it on this interval while the tab is open.
const SENT_REFRESH_MS = 5000;

// How often the tab re-checks that uploaded records are still in Firebase (lib/firebaseCheck.js):
// one read per match document, so a few minutes keeps it cheap.
const FIREBASE_CHECK_MS = 3 * 60 * 1000;

async function readMatchDoc(path) {
    const snap = await getDoc(doc(db, ...path));
    return snap.exists() ? snap.data() : null;
}

/** Both queues, each record tagged with which one it is in (in memory only). */
function readQueue(eventPath) {
    return STREAMS.flatMap(stream => getStreamQueue(eventPath, stream).map(r => ({ ...r, _stream: stream })));
}

/** Ids are unique across both queues, so one set per destination covers both. */
function readSent(eventPath) {
    const union = (read) => new Set(STREAMS.flatMap(stream => [...read(eventPath, stream)]));
    return {
        firebase: getFirebaseUploadedIds(eventPath),
        pi: union(getAcceptedIds),
        piRefused: union(getRejectedIds),
    };
}

/** Enough to place it in Firebase: which match document, and which slot in it. */
function canUpload(record, stream) {
    if (!record.match_number || !record.alliance_color) return false;
    return stream === 'timekeeper' || Boolean(record.team_number);
}

/** What still needs this record, for the confirmations before a delete. */
function notYetSentTo(record, sent) {
    const missing = [];
    if (!sent.pi.has(piKey(record))) missing.push('the Pi');
    if (!sent.firebase.has(record.id)) missing.push('Firebase');
    return missing;
}

function Relay({ currentTab, setCurrentTab, eventPath, settingsYear, funnySounds, sfxEnabled, onlineStatus }) {
    const { authentication } = useAuth();
    const [queue, setQueue] = useState([]);
    const [sent, setSent] = useState(() => readSent(eventPath));
    const [lastImport, setLastImport] = useState(null);
    const [uploading, setUploading] = useState(false);
    const folderInputRef = useRef(null);
    const [editingId, setEditingId] = useState(null);
    const [editingStream, setEditingStream] = useState('scouting');
    const [editBuffer, setEditBuffer] = useState({});
    const [checks, setChecks] = useState(() => getChecks(eventPath));

    /** Look the uploaded records up again; warn about any newly deleted in Firebase. */
    const runFirebaseCheck = useCallback(async () => {
        if (!authentication || !onlineStatus || !eventPath) return;
        const uploaded = getFirebaseUploadedIds(eventPath);
        const records = readQueue(eventPath).filter(r => uploaded.has(r.id));
        if (records.length === 0) return;
        const before = getChecks(eventPath);
        const results = await checkUploaded(records, eventPath, readMatchDoc);
        const newlyDeleted = Object.entries(results)
            .filter(([id, state]) => state === 'deleted' && before[id]?.state !== 'deleted').length;
        setChecks(saveChecks(eventPath, results));
        if (newlyDeleted > 0) {
            Toast.warning(`${newlyDeleted} uploaded record${newlyDeleted !== 1 ? 's were' : ' was'} deleted in Firebase - see the Firebase column`);
        }
    }, [authentication, onlineStatus, eventPath]);

    useEffect(() => {
        setChecks(getChecks(eventPath));
        if (currentTab !== 'relay') return undefined;
        runFirebaseCheck().catch(() => {});
        const id = setInterval(() => runFirebaseCheck().catch(() => {}), FIREBASE_CHECK_MS);
        return () => clearInterval(id);
    }, [currentTab, eventPath, runFirebaseCheck]);

    const refreshQueue = useCallback(() => {
        setQueue(readQueue(eventPath));
        setSent(readSent(eventPath));
    }, [eventPath]);

    useEffect(() => {
        if (currentTab !== 'relay') return undefined;
        refreshQueue();
        const id = setInterval(() => setSent(readSent(eventPath)), SENT_REFRESH_MS);
        return () => clearInterval(id);
    }, [currentTab, refreshQueue, eventPath]);

    const handleUpload = useCallback(async () => {
        if (!authentication) {
            Toast.error('Sign in to upload', funnySounds, sfxEnabled);
            return;
        }
        if (!onlineStatus) {
            Toast.error('No internet connection', funnySounds, sfxEnabled);
            return;
        }
        if (!eventPath) {
            Toast.error('No event path set', funnySounds, sfxEnabled);
            return;
        }

        // Only what this laptop has not uploaded yet - new records, and edited ones -
        // from both queues: scouting to normal_data, Timekeeper to timekeeper_data.
        const toUpload = STREAMS.flatMap(stream => pendingForFirebase(eventPath, stream).map(record => ({ stream, record })));
        if (toUpload.length === 0) {
            Toast.info('Everything in the queue is already uploaded');
            return;
        }

        setUploading(true);
        const successIds = [];
        let failed = 0;
        let alreadyThere = 0; // unedited and already in Firebase: left alone, counts as done

        for (const { stream, record } of toUpload) {
            const { id, _queued_at, _gamemode, _edited_at, ...dataObject } = record;
            if (!canUpload(dataObject, stream)) {
                failed++;
                continue;
            }
            try {
                const outcome = await writeToFirestore(dataObject, eventPath, _gamemode ?? 2, stream, { edited: Boolean(_edited_at) });
                if (outcome === 'exists') alreadyThere++;
                successIds.push(id);
            } catch (err) {
                console.error('Relay upload failed for record', id, err);
                failed++;
            }
        }

        // Kept in the queue: the Pi may not have these yet, and the Octopus uploader reads
        // the same queue. Only × and Clear Queue remove a record.
        markFirebaseUploaded(successIds, eventPath);
        refreshQueue();
        setUploading(false);
        // Re-read straight away, so a slot someone already deleted shows up now.
        runFirebaseCheck().catch(() => {});
        toast.dismiss(RELAY_TOAST_ID);

        const written = successIds.length - alreadyThere;
        if (written > 0) {
            Toast.success(`${written} record${written !== 1 ? 's' : ''} uploaded`);
        }
        if (alreadyThere > 0) {
            Toast.info(`${alreadyThere} already in Firebase - not overwritten (edit a record to replace it)`);
        }
        if (failed > 0) {
            Toast.error(`${failed} record${failed !== 1 ? 's' : ''} failed`, funnySounds, sfxEnabled);
        }
    }, [authentication, onlineStatus, eventPath, funnySounds, sfxEnabled, refreshQueue, runFirebaseCheck]);

    const handleUploadRef = useRef(handleUpload);
    useEffect(() => { handleUploadRef.current = handleUpload; }, [handleUpload]);

    const processImportedRecords = useCallback((allRecords) => {
        if (allRecords.length === 0) {
            Toast.info('No valid scout-queue files found in folder');
            return;
        }
        const added = mergeIntoQueue(allRecords, eventPath);
        refreshQueue();
        setLastImport(Date.now());
        Toast.success(`${added} new record${added !== 1 ? 's' : ''} added (${allRecords.length} total across files)`);


    }, [eventPath, refreshQueue]);

    async function handleImportFolder() {
        // Chrome / Edge: show directory picker directly
        if (window.showDirectoryPicker) {
            try {
                const dirHandle = await window.showDirectoryPicker({ mode: 'read' });
                const allRecords = [];
                for await (const [name, entry] of dirHandle.entries()) {
                    if (entry.kind !== 'file') continue;
                    if (!name.endsWith('.json') || !name.startsWith('scout-queue-')) continue;
                    try {
                        const file = await entry.getFile();
                        const text = await file.text();
                        allRecords.push(...parseQueueFile(text));
                    } catch { /* skip unreadable files */ }
                }
                processImportedRecords(allRecords);
                return;
            } catch (err) {
                if (err.name === 'AbortError') return;
                // any other error — fall through to the input fallback
            }
        }
        // Safari / Firefox fallback: <input webkitdirectory>
        folderInputRef.current?.click();
    }

    async function handleFolderInput(e) {
        const files = Array.from(e.target.files).filter(
            f => f.name.endsWith('.json') && f.name.startsWith('scout-queue-')
        );
        const allRecords = [];
        for (const file of files) {
            try {
                const text = await file.text();
                allRecords.push(...parseQueueFile(text));
            } catch { /* skip */ }
        }
        processImportedRecords(allRecords);
        e.target.value = '';
    }

    function handleClearQueue() {
        const notOnPi = queue.filter(r => !sent.pi.has(piKey(r))).length;
        const notUploaded = queue.filter(r => !sent.firebase.has(r.id)).length;
        const warnings = [];
        if (notOnPi > 0) warnings.push(`${notOnPi} not on the Pi yet`);
        if (notUploaded > 0) warnings.push(`${notUploaded} not uploaded to Firebase yet`);
        const warning = warnings.length ? `\n\n${warnings.join(', ')} - they will be lost.` : '';
        if (!window.confirm(`Clear all ${queue.length} record${queue.length !== 1 ? 's' : ''} from the queue for ${eventPath || 'this event'}?${warning}`)) return;
        STREAMS.forEach(stream => clearQueue(eventPath, stream));
        refreshQueue();
        Toast.success('Queue cleared');
    }

    function deleteRecord(record) {
        const { id } = record;
        const missing = notYetSentTo(record, sent);
        if (missing.length > 0
            && !window.confirm(`This record has not reached ${missing.join(' or ')} yet. Remove it anyway?`)) return;
        removeFromQueue([id], eventPath, record._stream);
        if (editingId === id) { setEditingId(null); setEditBuffer({}); }
        refreshQueue();
    }

    function startEdit(record) {
        setEditingId(record.id);
        setEditingStream(record._stream);
        setEditBuffer({
            _gamemode: record._gamemode ?? 2,
            match_number: record.match_number ?? '',
            team_number: record.team_number ?? '',
            alliance_color: record.alliance_color ?? 'r',
            team_position: record.team_position ?? 1,
            scout_id: record.scout_id ?? '',
        });
    }

    function saveEdit() {
        updateRecord(editingId, editBuffer, eventPath, editingStream);
        setEditingId(null);
        setEditBuffer({});
        refreshQueue();
    }

    function cancelEdit() {
        setEditingId(null);
        setEditBuffer({});
    }

    if (currentTab !== 'relay') return null;

    const pendingFirebase = queue.filter(r => !sent.firebase.has(r.id)).length;

    return (
        <div className={styles.Relay}>
            <Backarrow setCurrentTab={setCurrentTab} />
            <h2 className={styles.title}>Relay</h2>

            <div className={styles.summary}>
                <span className={styles.count}>
                    {queue.length} record{queue.length !== 1 ? 's' : ''} in queue
                    {eventPath ? <span className={styles.eventTag}> · {eventPath}</span> : null}
                </span>
                {queue.length > 0 && (
                    <span className={styles.lastImport}>
                        {queue.filter(r => sent.firebase.has(r.id)).length} uploaded to Firebase
                        {' · '}
                        {queue.filter(r => sent.pi.has(piKey(r))).length} on the Pi
                    </span>
                )}
                {lastImport && (
                    <span className={styles.lastImport}>
                        Last import: {new Date(lastImport).toLocaleTimeString()}
                    </span>
                )}
            </div>

            <div className={styles.actions}>
                <button
                    className="uiButton"
                    onClick={async () => {
                        const saved = await exportQueue(eventPath);
                        if (saved) Toast.success('Queue saved — transfer file to relay device');
                    }}
                    disabled={queue.length === 0}
                >
                    Export Queue
                </button>
                {authentication && (
                    <>
                        <button
                            className="uiButton"
                            onClick={handleImportFolder}
                        >
                            Import Folder
                        </button>
                        {/* webkitdirectory fallback for Safari/Firefox */}
                        <input
                            ref={folderInputRef}
                            type="file"
                            // eslint-disable-next-line react/no-unknown-property
                            webkitdirectory=""
                            multiple
                            accept=".json"
                            style={{ display: 'none' }}
                            onChange={handleFolderInput}
                        />
                        <button
                            className="uiButton"
                            onClick={handleUpload}
                            disabled={queue.length === 0 || uploading}
                        >
                            {uploading ? 'Uploading…' : `Upload Queue${pendingFirebase > 0 ? ` (${pendingFirebase})` : ''}`}
                        </button>
                    </>
                )}
                <button
                    className={`uiButton ${styles.clearBtn}`}
                    onClick={handleClearQueue}
                    disabled={queue.length === 0}
                >
                    Clear Queue
                </button>
            </div>

            <div className={styles.tableWrapper}>
                {queue.length === 0 ? (
                    <p className={styles.empty}>Queue is empty — press Submit after scouting a match, or on the Timekeeper page</p>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Match</th>
                                <th>Team</th>
                                <th>Position</th>
                                <th>Scout</th>
                                <th>Time</th>
                                <th>Sent</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {queue.map(record => {
                                const allianceClass = record.alliance_color === 'r' ? styles.redRow : styles.blueRow;
                                const isEditing = editingId === record.id;
                                if (isEditing) {
                                    return (
                                        <tr key={record.id} className={allianceClass}>
                                            <td>
                                                <select
                                                    className={styles.editSelect}
                                                    value={editBuffer._gamemode}
                                                    onChange={e => setEditBuffer(b => ({ ...b, _gamemode: parseInt(e.target.value) }))}
                                                >
                                                    <option value={0}>Prescouting</option>
                                                    <option value={1}>Practice</option>
                                                    <option value={2}>Quals</option>
                                                    <option value={3}>Elims</option>
                                                </select>
                                            </td>
                                            <td>
                                                <input
                                                    className={styles.editInput}
                                                    type="number"
                                                    value={editBuffer.match_number}
                                                    onChange={e => setEditBuffer(b => ({ ...b, match_number: e.target.value }))}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    className={styles.editInput}
                                                    type="number"
                                                    value={editBuffer.team_number}
                                                    onChange={e => setEditBuffer(b => ({ ...b, team_number: e.target.value }))}
                                                />
                                            </td>
                                            <td className={styles.positionEdit}>
                                                <select
                                                    className={styles.editSelect}
                                                    value={editBuffer.alliance_color}
                                                    onChange={e => setEditBuffer(b => ({ ...b, alliance_color: e.target.value }))}
                                                >
                                                    <option value="b">Blue</option>
                                                    <option value="r">Red</option>
                                                </select>
                                                <select
                                                    className={styles.editSelect}
                                                    value={editBuffer.team_position}
                                                    onChange={e => setEditBuffer(b => ({ ...b, team_position: parseInt(e.target.value) }))}
                                                >
                                                    {editBuffer._gamemode === 3 ? (
                                                        <>
                                                            <option value={1}>Captain</option>
                                                            <option value={2}>Pick 1</option>
                                                            <option value={3}>Pick 2</option>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <option value={1}>1</option>
                                                            <option value={2}>2</option>
                                                            <option value={3}>3</option>
                                                        </>
                                                    )}
                                                </select>
                                            </td>
                                            <td>
                                                <input
                                                    className={`${styles.editInput} ${styles.editInputWide}`}
                                                    type="text"
                                                    value={editBuffer.scout_id}
                                                    onChange={e => setEditBuffer(b => ({ ...b, scout_id: e.target.value }))}
                                                />
                                            </td>
                                            <td>{record._queued_at ? new Date(record._queued_at).toLocaleTimeString() : '—'}</td>
                                            <td><SentCell record={record} sent={sent} check={checks[record.id]} /></td>
                                            <td className={styles.actionCell}>
                                                <button className={`${styles.actionBtn} ${styles.saveBtn}`} onClick={saveEdit} title="Save">✓</button>
                                                <button className={`${styles.actionBtn} ${styles.cancelBtn}`} onClick={cancelEdit} title="Cancel">✕</button>
                                            </td>
                                        </tr>
                                    );
                                }
                                return (
                                    <tr key={record.id} className={allianceClass}>
                                        <td>
                                            {getMatchTypeLabel(record._gamemode)}
                                            {record._stream === 'timekeeper' && <span className={styles.streamTag}> · Timekeeper</span>}
                                        </td>
                                        <td>{record.match_number}</td>
                                        <td>{record.team_number}</td>
                                        <td>{getPositionLabel(record.alliance_color, record.team_position, record._gamemode)}</td>
                                        <td>{record.scout_id}</td>
                                        <td>{record._queued_at ? new Date(record._queued_at).toLocaleTimeString() : '—'}</td>
                                        <td><SentCell record={record} sent={sent} check={checks[record.id]} /></td>
                                        <td className={styles.actionCell}>
                                            <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => deleteRecord(record)} title="Remove">×</button>
                                            <button className={styles.actionBtn} onClick={() => startEdit(record)} title="Edit">
                                                <img src={editImg} alt="edit" className={styles.editIcon} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

/** Where this record has gone: Firebase (this tab's uploads) and the Pi (the Octopus uploader). */
const FIREBASE_CHECK_LABELS = {
    deleted: ['✕', 'Uploaded, then deleted in Firebase - kept here; re-upload it if that was a mistake'],
    changed: ['✎', 'Edited in Firebase since this was uploaded - that edit stands'],
};

function SentCell({ record, sent, check }) {
    const onFirebase = sent.firebase.has(record.id);
    const flagged = onFirebase && check && FIREBASE_CHECK_LABELS[check.state];
    const pi = sent.pi.has(piKey(record)) ? '✓' : sent.piRefused.has(piKey(record)) ? '✕' : '…';
    const piTitle = pi === '✓' ? 'On the Pi' : pi === '✕' ? 'The Pi refused this record' : 'Waiting for the Pi';
    return (
        <span className={styles.sentCell}>
            <span
                className={flagged && check.state === 'deleted' ? styles.firebaseDeleted : undefined}
                title={flagged ? flagged[1] : onFirebase ? 'Uploaded to Firebase' : 'Not uploaded to Firebase yet'}
            >
                Firebase {flagged ? flagged[0] : onFirebase ? '✓' : '…'}
            </span>
            <span title={piTitle}>Pi {pi}</span>
        </span>
    );
}

export default Relay;
